import React from 'react'

import { Circle, Group, Shape, Text, Image as KonvaImage } from 'react-konva'
import useImage from 'use-image'
import { AI_BOT_USER_ID } from 'shared/constants/ai'

import { useAppId } from '../../../../shared/hooks'

interface AnnotationMarkerProps {
  annotation: any
  isActive: boolean
  position: { x: number, y: number }
  squareSize: number
  markerDirection: 'bottom-left' | 'bottom-right' | 'top-left'
  currentScale: number
  onClick: (annotation: any) => void
  onMouseEnter: () => void
  onMouseLeave: () => void
}

const getAuthorAbbreviation = (authorName: string) => {
  return authorName[0].toUpperCase()
}

// Функция для создания формы маркера
const renderShape = (context, shape) => {
  const width = shape.width()
  const radius = width / 2

  context.beginPath()
  context.arc(0, 0, radius, -Math.PI, -Math.PI - Math.PI / 2, false)
  context.lineTo(-radius, radius)
  context.closePath()
  context.fillStrokeShape(shape)
}

// Компонент для отображения аватара или инициала
const AvatarContent: React.FC<{
  avatarUrl?: string
  authorName: string
  authorId: string
  squareSize: number
  currentScale: number
}> = ({ avatarUrl, authorName, authorId, squareSize, currentScale }) => {
  const { isAivard } = useAppId()
  const [image, status] = useImage(avatarUrl || '', 'anonymous')
  console.log('avatar content', avatarUrl)
  const avatarSize = squareSize - 10 // оставляем отступ от границ

  // Проверяем, является ли автор AI ботом
  const isAIBot = String(authorId) === String(AI_BOT_USER_ID)

  // Для AI бота используем специальный аватар
  if (isAIBot) {
    const aiBotAvatarSrc = isAivard ? '/icons/aivard-avatar.svg' : '/icons/yagel/yagel-avatar.png'
    const [aiImage, aiStatus] = useImage(aiBotAvatarSrc, 'anonymous')

    if (aiStatus === 'loaded' && aiImage) {
      return (
        <KonvaImage
          image={aiImage}
          width={avatarSize}
          height={avatarSize}
          offsetX={avatarSize / 2}
          offsetY={avatarSize / 2}
          cornerRadius={avatarSize / 2}
          pixelRatio={currentScale}
        />
      )
    }
  }

  if (avatarUrl && status === 'loaded' && image) {
    return (
      <KonvaImage
        image={image}
        width={avatarSize}
        height={avatarSize}
        offsetX={avatarSize / 2}
        offsetY={avatarSize / 2}
        cornerRadius={avatarSize / 2} // делаем круглым
        pixelRatio={currentScale}
        crop={{
          x: 0,
          y: 0,
          width: image.width,
          height: image.height
        }}
      />
    )
  }

  // Fallback к тексту если нет аватара или он не загрузился
  return (
    <Text
      text={getAuthorAbbreviation(authorName)}
      fill="white"
      fontSize={14}
      align="center"
      verticalAlign="middle"
      width={squareSize}
      height={squareSize}
      offsetX={squareSize / 2}
      offsetY={squareSize / 2 - 1}
    />
  )
}

export const AnnotationMarker: React.FC<AnnotationMarkerProps> = ({
  annotation,
  isActive,
  position,
  squareSize,
  markerDirection,
  currentScale,
  onClick,
  onMouseEnter,
  onMouseLeave
}) => {
  const { isAivard } = useAppId()
  const colorPrimary = isAivard ? '#8747DF' : '#605dff'
  const strokeColor = isActive ? colorPrimary : "#FFFFFF"
  const markerColor = "#FFFFFF"
  const customShapeStrokeWidth = 2
  if (isActive) {
    return (
      <Group
        x={position.x}
        y={position.y}
        onClick={() => onClick(annotation)}
        onTap={() => onClick(annotation)}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        draggable={false}
      >
        <Shape
          width={squareSize}
          height={squareSize}
          sceneFunc={renderShape}
          fill={markerColor}
          stroke={strokeColor}
          strokeWidth={customShapeStrokeWidth}
          scaleX={markerDirection === 'bottom-right' ? -1 : 1}
          scaleY={markerDirection === 'top-left' ? -1 : 1}
          perfectDrawEnabled={true}
          listening={true}
          cached={true}
          pixelRatio={currentScale}
        />

        <Circle
          radius={squareSize / 2 - 5}
          fill="rgba(0, 0, 0, 0.45)"
        />

        <AvatarContent
          avatarUrl={annotation.data.author_avatar_url}
          authorName={annotation.data.author_name}
          authorId={annotation.data.author_id}
          squareSize={squareSize}
          currentScale={currentScale}
        />
      </Group>
    )
  }

  return (
    <Group
      x={position.x}
      y={position.y}
      onClick={() => onClick(annotation)}
      onTap={() => onClick(annotation)}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      draggable={false}
    >
      {/* Размытая тень маркера - несколько слоев для эффекта размытия */}
      <Shape
        width={squareSize}
        height={squareSize}
        sceneFunc={renderShape}
        fill="rgba(0, 0, 0, 0.1)"
        stroke="rgba(0, 0, 0, 0.05)"
        strokeWidth={customShapeStrokeWidth}
        x={4}
        y={4}
        scaleX={markerDirection === 'bottom-right' ? -1 : 1}
        scaleY={markerDirection === 'top-left' ? -1 : 1}
        perfectDrawEnabled={true}
        listening={false}
        cached={true}
        pixelRatio={currentScale}
      />

      <Shape
        width={squareSize}
        height={squareSize}
        sceneFunc={renderShape}
        fill="rgba(0, 0, 0, 0.15)"
        stroke="rgba(0, 0, 0, 0.08)"
        strokeWidth={customShapeStrokeWidth}
        x={3}
        y={3}
        scaleX={markerDirection === 'bottom-right' ? -1 : 1}
        scaleY={markerDirection === 'top-left' ? -1 : 1}
        perfectDrawEnabled={true}
        listening={false}
        cached={true}
        pixelRatio={currentScale}
      />

      <Shape
        width={squareSize}
        height={squareSize}
        sceneFunc={renderShape}
        fill="rgba(0, 0, 0, 0.2)"
        stroke="rgba(0, 0, 0, 0.12)"
        strokeWidth={customShapeStrokeWidth}
        x={2}
        y={3}
        scaleX={markerDirection === 'bottom-right' ? -1 : 1}
        scaleY={markerDirection === 'top-left' ? -1 : 1}
        perfectDrawEnabled={true}
        listening={false}
        cached={true}
        pixelRatio={currentScale}
      />

      <Shape
        width={squareSize}
        height={squareSize}
        sceneFunc={renderShape}
        fill={markerColor}
        stroke={markerColor}
        strokeWidth={customShapeStrokeWidth}
        scaleX={markerDirection === 'bottom-right' ? -1 : 1}
        scaleY={markerDirection === 'top-left' ? -1 : 1}
        perfectDrawEnabled={true}
        listening={true}
        cached={true}
        pixelRatio={currentScale}
      />

      <Circle
        radius={squareSize / 2 - 5}
        fill="rgba(0, 0, 0, 0.45)"
      />

      <AvatarContent
        avatarUrl={annotation.data.author_avatar_url}
        authorName={annotation.data.author_name}
        authorId={annotation.data.author_id}
        squareSize={squareSize}
        currentScale={currentScale}
      />
    </Group>
  )
}
